import React, { useState } from "react";
import {
  Avatar,
  AvatarGroup,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Tooltip,
} from "@fluentui/react-components";
import PropTypes from "prop-types";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import "./deal-team-avatar.scss";

const memberShape = PropTypes.shape({
  IsOwner: PropTypes.bool,
  IsStaffer: PropTypes.bool,
  IsSeniorDealMember: PropTypes.bool,
  User: PropTypes.shape({
    Name: PropTypes.string,
  }),
});

const getColor = (isOwner, isSenior, isStaffer) => (isOwner || isStaffer ? "brand" : isSenior ? "seafoam" : "blue");

const getMemberCategory = (member) => {
  if (member.IsOwner) return 0;
  if (member.IsStaffer) return 1;
  if (member.IsSeniorDealMember) return 3;
  return 2;
};

const sortMembers = (members) => {
  return [...members].sort((a, b) => getMemberCategory(a) - getMemberCategory(b));
};

const AvatarWithBadge = ({ member, size = 24 }) => {
  const isStafferNonOwner = member.IsStaffer && !member.IsOwner;

  return (
    <Avatar
      name={member.User?.Name || "N/A"}
      color={getColor(member.IsOwner, member?.IsSeniorDealMember, member?.IsStaffer)}
      size={size}
      badge={isStafferNonOwner ? { status: "blocked" } : undefined}
      title={member.User?.Name || "N/A"}
    />
  );
};

AvatarWithBadge.propTypes = {
  member: memberShape.isRequired,
  size: PropTypes.number,
};

const DealTeamAvatarGroupCell = ({ members }) => {
  const [open, setOpen] = useState(false);

  if (!members || !Array.isArray(members) || members.length === 0) {
    return <span>–</span>;
  }

  const sortedMembers = sortMembers(members);
  const shownMembers = sortedMembers.slice(0, 3);
  const extraMembers = sortedMembers.length > 3 ? sortedMembers.length - 3 : 0;

  const handleMouseEnter = () => setOpen(true);
  const handleMouseLeave = () => setOpen(false);

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Popover
        open={open}
        onOpenChange={(e, data) => setOpen(data.open)}
        positioning={{ position: "below-start" }}
      >
        <PopoverTrigger disableButtonEnhancement>
          <div className="deal-team-avatar__trigger">
            <AvatarGroup layout="stack">
              {shownMembers.map((member, index) => (
                <div
                  key={index}
                  className="deal-team-avatar__stacked-item"
                  style={{ zIndex: 10 - index }}
                >
                  <AvatarWithBadge member={member} size={24} />
                </div>
              ))}
              {extraMembers > 0 && (
                <div className="deal-team-avatar__stacked-item--extra">
                  <Avatar
                    initials={`+${extraMembers}`}
                    color="neutral"
                    size={24}
                  />
                </div>
              )}
            </AvatarGroup>
          </div>
        </PopoverTrigger>

        <PopoverSurface>
          <div className="deal-team-avatar__popover-list">
            {sortedMembers.map((member, index) => {
              const isStafferMember = member?.IsStaffer && !member?.IsOwner;
              return (
                <div key={index} className="deal-team-avatar__popover-row">
                  <AvatarWithBadge member={member} size={24} />
                  <span className="deal-team-avatar__member-name">
                    {member?.User?.Name || "N/A"}
                  </span>
                  {isStafferMember && (
                    <Tooltip
                      content="This Staffer is a Member on this Deal, not an Owner. A Deal Owner can reassign them as an Owner to give them full Staffing permissions."
                      relationship="description"
                      withArrow
                    >
                      <span
                        tabIndex={-1}
                        aria-label="Member info"
                        className="deal-team-avatar__member-info-icon"
                      >
                        <FluentUiIcon name="InfoRegular" size={14} color="#8a8a8a" />
                      </span>
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
};

DealTeamAvatarGroupCell.propTypes = {
  members: PropTypes.arrayOf(memberShape),
};

export default DealTeamAvatarGroupCell;
